import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { Control, FieldError, FieldValues, Merge, Path } from 'react-hook-form';
import { EventType, PaginatedData, Tournament } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import { getData } from '../../utils/api';
import FormSelect from '../molecules/FormSelect';

type TournamentSelectProps<T extends FieldValues, TTransformedValues extends FieldValues | undefined = T> = {
  control: Control<T, unknown, TTransformedValues>;
  name: Path<T>;
  label?: string;
  required?: boolean;
  competitionIds?: string | string[];
  /** Limits options to tournaments that have at least one event of this type. */
  eventType?: EventType;
  error?: Merge<FieldError, (FieldError | undefined)[]>;
  multiple?: boolean;
  closeMenuOnSelect?: boolean;
};

const TournamentSelect = <T extends FieldValues, TTransformedValues extends FieldValues | undefined = T>({
  control,
  name,
  label,
  required,
  competitionIds,
  eventType,
  error,
  multiple,
  closeMenuOnSelect,
}: TournamentSelectProps<T, TTransformedValues>) => {
  const { data: tournamentsData } = useQuery({
    queryKey: [QUERY_KEYS.tournaments, competitionIds, eventType],

    queryFn: (): Promise<PaginatedData<Tournament>> =>
      getData('tournaments', {
        limit: 100,
        page: 1,
        competitionIds,
        eventType,
      }),

    enabled: !isEmpty(competitionIds),
  });

  return (
    <FormSelect
      label={label ?? 'Tournament'}
      name={name}
      control={control}
      required={required}
      options={tournamentsData?.items || []}
      disabled={isEmpty(competitionIds)}
      error={error}
      multiple={multiple}
      closeOnSelect={closeMenuOnSelect}
    />
  );
};

export default TournamentSelect;
